import { ensureBaseURL } from '@philips-internal/oswald-common-utils';
import { ThemeColor, ThemeTonalRange } from '@philips-internal/oswald-ui-constants';
import { getViewportElement, isContentfulString } from '@philips-internal/oswald-ui-utils';
import { ConfigurationService } from '../configuration-service/configuration-service.js';
const COLOR_MAP = new Map([
    ['aqua', ThemeColor.AQUA],
    ['blue', ThemeColor.BLUE],
    ['green', ThemeColor.GREEN],
    ['grey', ThemeColor.GREY],
    ['group-blue', ThemeColor.GROUP_BLUE],
    ['orange', ThemeColor.ORANGE],
    ['pink', ThemeColor.PINK],
    ['purple', ThemeColor.PURPLE],
]);
const TONAL_RANGE_MAP = new Map([
    ['bright', ThemeTonalRange.BRIGHT],
    ['ultra-light', ThemeTonalRange.ULTRA_LIGHT],
    ['very-dark', ThemeTonalRange.VERY_DARK],
    ['very-light', ThemeTonalRange.VERY_LIGHT],
]);
const supportedAccents = {
    [ThemeColor.AQUA]: [ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
    [ThemeColor.BLUE]: [ThemeColor.AQUA, ThemeColor.GREEN, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
    [ThemeColor.GREEN]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
    [ThemeColor.GREY]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
    [ThemeColor.GROUP_BLUE]: [ThemeColor.AQUA, ThemeColor.GREEN, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
    [ThemeColor.ORANGE]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.PINK, ThemeColor.PURPLE],
    [ThemeColor.PINK]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PURPLE],
    [ThemeColor.PURPLE]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PINK],
};
const accentColorRegex = /t-color-accent-[a-z-]*/;
const colorRegex = /t-color-[a-z-]*/;
const contentTonalRangeRegex = /t-content-[a-z-]*/;
const navigationTonalRangeRegex = /t-nav-[a-z-]*/;
function createThemeService() {
    const viewport = getViewportElement(true);
    const getColorFromString = (color) => {
        return COLOR_MAP.get(color);
    };
    const getTonalRangeFromString = (tonalRange) => {
        return TONAL_RANGE_MAP.get(tonalRange);
    };
    const getSupportedAccents = (color) => {
        return supportedAccents[color];
    };
    const getTheme = () => {
        const className = viewport.className;
        const [accentColorStr] = className.match(accentColorRegex) ?? [];
        const [colorStr] = className.match(colorRegex) ?? [];
        const [contentTonalRangeStr] = className.match(contentTonalRangeRegex) ?? [];
        const [navigationTonalRangeStr] = className.match(navigationTonalRangeRegex) ?? [];
        return {
            accentColor: accentColorStr ? getColorFromString(accentColorStr.replace('t-color-accent-', '')) : undefined,
            color: colorStr ? getColorFromString(colorStr.replace('t-color-', '')) : undefined,
            contentTonalRange: contentTonalRangeStr ? getTonalRangeFromString(contentTonalRangeStr.replace('t-content-', '')) : undefined,
            navigationTonalRange: navigationTonalRangeStr ? getTonalRangeFromString(navigationTonalRangeStr.replace('t-nav-', '')) : undefined,
        };
    };
    const removeTheme = (options) => {
        const { accentColor = false, color = false, content = false, navigation = false } = options;
        let className = viewport.className;
        if (accentColor) {
            className = className.replace(accentColorRegex, '');
        }
        if (color) {
            className = className.replace(colorRegex, '');
        }
        if (content) {
            className = className.replace(contentTonalRangeRegex, '');
        }
        if (navigation) {
            className = className.replace(navigationTonalRangeRegex, '');
        }
        viewport.className = className.trim();
    };
    const addColorClassesToViewport = (color, accentColor) => {
        const colorClass = `t-color-${color}`;
        const accentColorClass = `t-color-accent-${accentColor}`;
        removeTheme({ accentColor: true, color: true });
        viewport.classList.add(colorClass, accentColorClass);
    };
    const setColors = (color, accentColor) => {
        const supportedAccents = getSupportedAccents(color);
        if (!supportedAccents.includes(accentColor)) {
            throw Error(`[Theme Service]: The color ${color} does not support the color accent ${accentColor}`);
        }
        addColorClassesToViewport(color, accentColor);
    };
    const createStylesheet = (stylesheetSource) => {
        const link = document.createElement('link');
        link.href = stylesheetSource;
        link.rel = 'stylesheet';
        link.type = 'text/css';
        document.head.appendChild(link);
    };
    const setCustomColors = async (color, accentColor) => {
        const themeColor = COLOR_MAP.get(color);
        const themeAccentColor = COLOR_MAP.get(accentColor);
        if (themeColor && themeAccentColor) {
            addColorClassesToViewport(themeColor, themeAccentColor);
            return;
        }
        // @ts-expect-error
        const { get } = await import('virtual:oswald-configuration');
        const runtime = await get();
        const stylesheets = runtime.customization?.stylesheets;
        if (!isContentfulString(stylesheets)) {
            throw Error(`[Theme Service]: The base stylesheet source is not included in the runtime configuration`);
        }
        const base = ConfigurationService.getBaseURL();
        const stylesheetSource = ensureBaseURL(stylesheets, base);
        if (!themeColor) {
            createStylesheet(`${stylesheetSource}/${color}.css`);
        }
        if (!themeAccentColor) {
            createStylesheet(`${stylesheetSource}/${accentColor}.css`);
        }
        addColorClassesToViewport(themeColor ?? color, themeAccentColor ?? accentColor);
    };
    const setNavigationTonalRange = (tonalRange) => {
        const navigationClass = `t-nav-${tonalRange}`;
        removeTheme({ navigation: true });
        viewport.classList.add(navigationClass);
    };
    const setContentTonalRange = (tonalRange) => {
        const contentClass = `t-content-${tonalRange}`;
        removeTheme({ content: true });
        viewport.classList.add(contentClass);
    };
    return {
        getColorFromString,
        getSupportedAccents,
        getTheme,
        getTonalRangeFromString,
        removeTheme,
        setColors,
        setContentTonalRange,
        setCustomColors,
        setNavigationTonalRange,
    };
}
const ThemeService = createThemeService();
export { ThemeService, };
//# sourceMappingURL=theme-service.js.map